/**
 * WriterFollowerChips — strip of "{title} · N followers · sparkline" cards,
 * one per series the scribe owns. Mounted at the top of the Writer
 * Dashboard so scribes can see their fanbase grow.
 */
import React, { useEffect, useState } from 'react';
import { Users, Film, Headphones, TrendingUp } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { authFetch } from '../utils/authUtils';
import Sparkline from './Sparkline';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const WriterFollowerChips = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/writer/follower-stats?days=30`);
        if (r.ok) setStats(await r.json());
      } catch {/* noop */}
      finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return null;
  const series = stats?.series || [];
  if (series.length === 0) return null;

  const totalAll = series.reduce((sum, s) => sum + (s.total || 0), 0);
  const newAll = series.reduce((sum, s) => sum + (s.new_in_period || 0), 0);

  return (
    <div className="mb-8" data-testid="writer-followers-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-fuchsia-300" />
          <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
            My followers
          </h3>
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-fuchsia-600/20 text-fuchsia-200 border border-fuchsia-500/40">
            {totalAll} total
          </span>
          {newAll > 0 && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-600/20 text-emerald-300 border border-emerald-500/40 inline-flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              +{newAll} in 30d
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {series.map((s) => {
          const Icon = s.type === 'cinematic' ? Film : Headphones;
          return (
            <Card
              key={s.series_id}
              className="bg-slate-800/50 border-slate-700"
              data-testid={`writer-follower-chip-${s.series_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-fuchsia-600/20 border border-fuchsia-500/40 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-fuchsia-200" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate" title={s.title}>
                      {s.title || '—'}
                    </p>
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-0.5">
                      {s.type}
                    </p>
                  </div>
                </div>

                <div className="mt-3 flex items-end justify-between gap-3">
                  <div>
                    <p className="text-2xl font-bold text-fuchsia-200 leading-none">
                      {s.total}
                    </p>
                    <p className="text-[10px] text-gray-500 mt-1">
                      followers
                      {s.new_in_period > 0 && (
                        <span className="ml-1 text-emerald-300">
                          (+{s.new_in_period} in 30d)
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="opacity-90">
                    <Sparkline
                      values={s.cumulative || []}
                      width={120}
                      height={32}
                      stroke="#e879f9"
                      fill="rgba(232,121,249,0.18)"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default WriterFollowerChips;
